import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import {
  getMarketplaceEnablement,
  hasProductCodeInMarketplaceEnablement,
  PLAN_ENABLEMENT_FLAG_NAMES,
} from '@gm-commercial/entitlements-client';
import { useMemo } from 'react';

import { ProductCodes } from '~/common/models/plans.model';

/** Hardware product codes that indicate Shop Add-Ons is available for the current context. */
const SHOP_ADD_ONS_HARDWARE_CODES = [
  ProductCodes.DUALCAM,
  ProductCodes.GTADAPTER,
];

export type UseShopAddOnsEnablementResult = {
  /**
   * True when user context is identified and the BPS marketplace flag contains
   * DUALCAM or GTADAPTER, meaning Shop Add-Ons hardware is purchasable in the
   * current org context.
   */
  isHardwareEnabledInMarketplace: boolean;
};

/**
 * Resolves Shop Add-Ons hardware availability from the BPS marketplace catalog flag.
 *
 * Uses only the raw marketplace flag (no plan intersection), since hardware
 * products (cameras, adapters) are not plan-gated — their presence in the
 * marketplace flag is the sole signal.
 *
 * @returns `isHardwareEnabledInMarketplace` — true once context is identified and
 *   DUALCAM or GTADAPTER appear in the marketplace map.
 */
export function useShopAddOnsEnablement(): UseShopAddOnsEnablementResult {
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();

  const marketplaceEnablement = useMemo(
    () =>
      isUserContextIdentified
        ? getMarketplaceEnablement(
            getFlag,
            PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE
          )
        : {},
    [getFlag, isUserContextIdentified]
  );

  const isHardwareEnabledInMarketplace = useMemo(
    () =>
      SHOP_ADD_ONS_HARDWARE_CODES.some((code) =>
        hasProductCodeInMarketplaceEnablement(marketplaceEnablement, [code])
      ),
    [marketplaceEnablement]
  );

  return { isHardwareEnabledInMarketplace };
}
